import { graphqlClient } from '@/lib/graphql-client';
import { omitScopeFields } from '@/lib/scope-stamp';
import {
  DELETE_LUNCH_MENU_MUTATION,
  INSERT_LUNCH_MENU_MUTATION,
  UPDATE_LUNCH_MENU_MUTATION,
} from '../graphql/mutations';
import { buildGetLunchMenusQuery, buildGetLunchRequestsQuery } from '../graphql/queries';
import {
  GetLunchMenusParams,
  GetLunchRequestsParams,
  LunchMenuInsertInput,
  LunchMenusResult,
  LunchMenuUpdateInput,
  LunchRequestsPagingResult,
} from '../types/manage-lunch.types';

export const getLunchMenus = async (
  params: GetLunchMenusParams
): Promise<{ LunchMenus: LunchMenusResult }> => {
  const { pageNo, pageSize, filter = {} } = params;
  return graphqlClient.query({
    query: buildGetLunchMenusQuery(filter, pageNo, pageSize),
  });
};

export const getLunchRequests = async (
  params: GetLunchRequestsParams
): Promise<{ LunchRequests: LunchRequestsPagingResult }> => {
  const { pageNo, pageSize, filter = {}, order } = params;
  return graphqlClient.query({
    query: buildGetLunchRequestsQuery(filter, pageNo, pageSize, order),
  });
};

/** Every request matching `filter`, not just one page: reads the total first, then fetches them all at once. */
export const getAllLunchRequests = async (filter: Record<string, unknown>) => {
  const first = await getLunchRequests({ pageNo: 1, pageSize: 1, filter });
  const totalCount = first.LunchRequests.totalCount;
  if (totalCount <= 1) return first.LunchRequests.items;
  const all = await getLunchRequests({ pageNo: 1, pageSize: totalCount, filter });
  return all.LunchRequests.items;
};

export const createLunchMenu = async (input: LunchMenuInsertInput) => {
  return graphqlClient.mutate({ query: INSERT_LUNCH_MENU_MUTATION, variables: { input } });
};

export const updateLunchMenu = async (params: { itemId: string; input: LunchMenuUpdateInput }) => {
  const filter = JSON.stringify({ _id: params.itemId });
  return graphqlClient.mutate({
    query: UPDATE_LUNCH_MENU_MUTATION,
    variables: { filter, input: omitScopeFields(params.input) },
  });
};

export const deleteLunchMenu = async (itemId: string) => {
  const filter = JSON.stringify({ _id: itemId });
  return graphqlClient.mutate({
    query: DELETE_LUNCH_MENU_MUTATION,
    variables: { filter, input: { isHardDelete: false } },
  });
};
